import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { globalToaster } from '../shared/components/GlobalToaster';
import { parseApolloError } from '../shared/utils/errorHandler';
import { markUnauthenticated } from '../shared/auth/authStore';

// Create HTTP link. `credentials: 'include'` sends the session cookie with each
// GraphQL request (same-origin in prod behind Caddy; harmless otherwise).
const httpLink = createHttpLink({
  uri: import.meta.env.VITE_APOLLO_CLIENT_URL,
  credentials: 'include',
});

// Error link to handle authentication errors
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // Create a mock ApolloError to use our error parser
  const mockError = {
    graphQLErrors: graphQLErrors || [],
    networkError,
    message: '',
    name: 'ApolloError',
    extraInfo: undefined,
    clientErrors: [],
    protocolErrors: []
  };

  // Session expired / not signed in: the backend answers GraphQL with 401.
  // Flip auth state and bounce to the login form instead of toasting an error.
  const status = (networkError as { statusCode?: number } | undefined)?.statusCode;
  if (status === 401) {
    markUnauthenticated();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    return;
  }

  const errorInfo = parseApolloError(mockError as any);

  // Show error toast for user-facing errors
  // Skip showing toasts for certain operations that handle errors locally
  const skipToast = operation.operationName === 'IntrospectionQuery' ||
                   operation.getContext().skipErrorToast;

  if (!skipToast) {
    globalToaster.error({
      message: errorInfo.message,
      timeout: errorInfo.type === 'auth' ? 7000 : 5000
    });
  }

  // Log errors for debugging
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// Access control happens at the reverse proxy (Caddy basic auth); the browser
// attaches those credentials to same-origin requests automatically, so no
// auth link is needed here.
const client = new ApolloClient({
  link: from([
    errorLink,
    httpLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          allBookings: {
            merge(_existing = [], incoming) {
              return incoming;
            },
          },
          allRoutes: {
            merge(_existing = [], incoming) {
              return incoming;
            },
          },
          allShippingSchedules: {
            merge(_existing = [], incoming) {
              return incoming;
            },
          },
          allTierPrices: {
            merge(_existing = [], incoming) {
              return incoming;
            },
          },
          routeById: {
            read(existing, { args, toReference }) {
              return existing || toReference({ __typename: 'Route', id: args?.id });
            },
          },
        },
      },
      Route: {
        fields: {
          routePortsByRouteId: {
            merge(_existing = { nodes: [], totalCount: 0 }, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-first',
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-first',
    },
  },
});

export default client;
